/**
 * Word drills use Leitner-style spaced repetition. Every word a reader
 * has seen sits in a box; a word they recall climbs a box and waits
 * longer before coming back, a word they forget drops to box 1 and
 * returns within minutes. Box 0 means "never drilled".
 */

export const DRILL_RATINGS = ["again", "good", "easy"] as const;
export type DrillRating = (typeof DRILL_RATINGS)[number];

export const MAX_BOX = 6;

/** Gap before the next review, indexed by the box a word lands in. */
const BOX_GAP_DAYS = [0, 1, 3, 7, 16, 35, 90] as const;

const AGAIN_GAP_MINUTES = 10;
const DAY_MS = 24 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

/** A word whose next review is two weeks or more away. */
const MASTERED_FROM_BOX = 4;

function clampBox(box: number): number {
  return Math.min(MAX_BOX, Math.max(0, Math.trunc(box)));
}

function landingBox(box: number, rating: DrillRating): number {
  const current = clampBox(box);
  if (rating === "again") return 1;
  return Math.min(MAX_BOX, current + (rating === "easy" ? 2 : 1));
}

function gapMs(box: number, rating: DrillRating): number {
  if (rating === "again") return AGAIN_GAP_MINUTES * MINUTE_MS;
  return BOX_GAP_DAYS[landingBox(box, rating)] * DAY_MS;
}

export interface DrillSchedule {
  box: number;
  dueAt: string;
  /** True when a word the reader had already learned was forgotten. */
  lapsed: boolean;
}

export function nextReview(
  box: number,
  rating: DrillRating,
  now: Date,
): DrillSchedule {
  return {
    box: landingBox(box, rating),
    dueAt: new Date(now.getTime() + gapMs(box, rating)).toISOString(),
    lapsed: rating === "again",
  };
}

export function isMastered(box: number): boolean {
  return clampBox(box) >= MASTERED_FROM_BOX;
}

/** Short label for the gap a rating would give, e.g. "10m", "3d". */
export function previewInterval(box: number, rating: DrillRating): string {
  if (rating === "again") return `${AGAIN_GAP_MINUTES}m`;
  const days = BOX_GAP_DAYS[landingBox(box, rating)];
  if (days >= 60) return `${Math.round(days / 30)}mo`;
  return `${days}d`;
}
